import bgImg from '~/assets/main-bg.png'
import partnerLogoImg from '~/assets/partner-logo.svg'
import plantImg from '~/assets/plant/product.png'

import { cn } from '@forest/shared'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import { createEffect, createSignal, onMount, Show, Suspense } from 'solid-js'
import { Countdown } from '~/components/countdown'
import { CountdownDialog } from '~/components/countdown-dialog'
import { CurrencyBar } from '~/components/currency-bar'
import { Divider } from '~/components/divider'
import { DonationSection } from '~/components/donation-section'
import { PlantDialog } from '~/components/exclusive-plant-dialog'
import { CheckmarkBadgeIcon } from '~/components/icon/checkmark-badge'
import { CloseIcon } from '~/components/icon/close'
import { FlagIcon } from '~/components/icon/flag'
import { InfoIcon } from '~/components/icon/info'
import { MotionDiv } from '~/components/motion-div'
import { Participant } from '~/components/participant'
import { Progress } from '~/components/progress'
import { getMaxEnergy } from '~/components/progress/config'
import { RewardFlow } from '~/components/reward-flow/reward-flow'
import { Spinner } from '~/components/spinner'
import { native } from '@forest/native-services'
import { m } from '~/paraglide/messages'
import {
  usePurchaseStats,
  useUserPurchaseStats,
} from '~/resources/purchase-stats'
import { showDialog } from '~/store/dialog'
import { FrontBgAnim } from './components/front-bg-anim'
import { eventEndDate, eventStartDate } from './config'

export function Page() {
  const [isPlantDialogOpen, setIsPlantDialogOpen] = createSignal(false)

  onMount(() => {
    native.general.logEvent({
      event_name: 'page_view',
      event_params: { campaign: 'earth_day_2026' },
    })
  })

  const purchaseStatsQuery = usePurchaseStats()
  const participantCountStr = () =>
    (purchaseStatsQuery.data?.stats?.[0]?.total_quantity ?? 0).toLocaleString()
  const totalEnergy = () => {
    const totalPrice = purchaseStatsQuery.data?.stats?.[0]?.total_price ?? 0
    return totalPrice * 2.5
  }
  const hasReachedLastCheckpoint = () => totalEnergy() >= getMaxEnergy()

  const userPurchaseStatsQuery = useUserPurchaseStats()
  const hasDonated = () => (userPurchaseStatsQuery.data?.stats?.length ?? 0) > 0
  const userDonatedPlan = (): number => {
    switch (userPurchaseStatsQuery.data?.stats?.[0]?.total_price ?? 0) {
      case 50:
        return 1
      case 100:
        return 2
      case 250:
        return 3
      case 800:
        return 4
      default:
        return 1
    }
  }

  createEffect(() => {
    const error = purchaseStatsQuery.error || userPurchaseStatsQuery.error
    if (!error) return
    if (
      isTimeoutError(error) ||
      (isNetworkError as (error: unknown) => boolean)(error)
    ) {
      showDialog({
        title: m.dialog_data_load_fail_title(),
        description: m.dialog_data_load_fail_description(),
        primaryButtonText: m.ok(),
        primaryAction: () => {
          native.general.close()
        },
      })
      return
    }
    showDialog({
      title: m.dialog_data_load_fail_title(),
      description: m.error_code_general({
        status: isHTTPError(error) ? error.response.status : error.message,
      }),
      primaryButtonText: m.ok(),
      primaryAction: () => {
        native.general.close()
      },
    })
  })

  const [isBgLoaded, setIsBgLoaded] = createSignal(false)
  const bg = new Image()
  bg.onload = () => {
    setIsBgLoaded(true)
  }
  bg.src = bgImg

  return (
    <>
      <CountdownDialog startDate={eventStartDate} treeImg={plantImg} />
      <div class="fixed right-4 top-[calc(16px+var(--safe-area-top))] z-[1] flex items-center gap-4">
        <Countdown endDate={eventEndDate} />
        <CurrencyBar />
      </div>

      {/* Background */}
      <div
        class="absolute bottom-0 left-0 right-0 top-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(0, 85, 78, 0.00) 0%, #002E3E 100%)',
        }}
      />
      <div
        class={cn(
          'pointer-events-none absolute left-0 right-0 top-0 h-[900px] transition-opacity min-[1192px]:aspect-[1192/900] min-[1192px]:h-auto min-[1192px]:w-screen',
          isBgLoaded() ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          'background-image': `url(${bgImg})`,
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position-x': 'center',
        }}
      />

      <Suspense
        fallback={
          <div class="fixed inset-0 grid place-items-center bg-black/30 backdrop-blur-sm">
            <Spinner class="size-10" />
          </div>
        }
      >
        <div class="relative pb-[72px] pt-[calc(72px+var(--safe-area-top))]">
          <div class="mx-auto max-w-[800px] space-y-9 px-6">
            <MotionDiv delay={0} class="space-y-3">
              <h1 class="text-shadow-[0px_0px_15px_rgba(255,255,255,0.50)] whitespace-pre-line text-[36px] font-bold text-white">
                {m.earth_day_2025_landing_title()}
              </h1>
              <p class="text-white">{m.earth_day_2025_landing_description()}</p>
            </MotionDiv>

            <MotionDiv delay={0.15}>
              <div class="relative h-[250px]">
                <div class="flex">
                  <h2
                    class="flex-1 text-xl text-white"
                    style={{
                      'text-shadow': '0 0 15px rgba(255,255,255,0.50)',
                    }}
                  >
                    {m.current_total_energy()}
                  </h2>
                  <button
                    class="flex h-7 shrink-0 items-center gap-1 rounded-full bg-white/15 px-[6px] text-white ring-1 ring-inset ring-white/80"
                    onClick={() => {
                      showDialog({
                        title: m.event_rule_title(),
                        description: () => (
                          <p class="text-left">{m.event_rule_description()}</p>
                        ),
                        primaryButtonText: m.ok(),
                      })
                    }}
                  >
                    <InfoIcon />
                    {m.event_rule_title()}
                  </button>
                </div>
                <div class="absolute -left-6 top-12 w-screen max-w-[900px]">
                  <Progress />
                </div>
              </div>
            </MotionDiv>

            <MotionDiv delay={0.3}>
              <button
                class={cn(
                  'mb-4 flex min-h-[84px] w-full items-center gap-3 rounded-3xl px-3 py-2 ring-1 ring-inset ring-white',
                  hasDonated() && 'gap-2',
                )}
                style={{
                  background: 'linear-gradient(125deg, #FFF 30%, #C5FFFB 74%)',
                }}
                onClick={() => {
                  setIsPlantDialogOpen(true)
                }}
              >
                <img class="size-[77px] shrink-0" src={plantImg} />
                <Show
                  when={hasDonated()}
                  fallback={
                    <p
                      class="flex-1 text-left text-[#037067]"
                      innerHTML={m.earth_day_2025_donate_content({
                        earthday_plant: m.tree_type_163_title(),
                      })}
                    />
                  }
                >
                  <p
                    class="flex-1 text-left text-[#037067]"
                    innerHTML={m.unlocked_successful({
                      earthday_plant: m.tree_type_163_title(),
                    })}
                  />
                  <div class="mr-3 shrink-0">
                    <CheckmarkBadgeIcon />
                  </div>
                </Show>
              </button>

              <Show when={hasReachedLastCheckpoint() && !hasDonated()}>
                <div class="mb-[10px] flex items-center gap-3">
                  <div class="flex h-[46px] items-center gap-1 rounded-lg px-3 ring-1 ring-inset ring-[#FDFFB4]">
                    <FlagIcon />
                    <span class="text-sm text-[#FDFFB4]">
                      {m.goal_reached()}
                    </span>
                  </div>
                  <p class="flex-1 text-xs text-white">
                    {m.goal_reached_description()}
                  </p>
                </div>
              </Show>

              <DonationSection
                hasDonated={hasDonated()}
                userDonatedPlan={userDonatedPlan()}
                refetchUserPurchaseStats={userPurchaseStatsQuery.refetch}
                refetchPurchaseStats={purchaseStatsQuery.refetch}
              />
            </MotionDiv>

            <MotionDiv delay={0.45} class="space-y-4">
              <Divider>{m.earth_day_2025_participant()}</Divider>
              <Participant participantCountStr={participantCountStr()} />
            </MotionDiv>

            <MotionDiv delay={0.6}>
              <Divider>
                <span class="text-[#00CEBD]">{m.earth_day_2025_partner()}</span>
              </Divider>
              <div class="mx-auto mt-4 flex h-[58px] justify-center">
                <img src={partnerLogoImg} />
              </div>
              <p
                class="mt-5 px-6 text-center text-sm text-[#008B7F]"
                innerHTML={m.earth_day_2025_partner_content({
                  sponsor_page: m.earth_day_2025_partner_sponsor_page(),
                  'sponsor page': m.earth_day_2025_partner_sponsor_page(),
                })}
              />
            </MotionDiv>
          </div>
        </div>
      </Suspense>

      <div class="pointer-events-none absolute left-0 right-0 top-0 h-[900px] min-[1192px]:aspect-[1192/900] min-[1192px]:h-auto min-[1192px]:w-screen">
        <FrontBgAnim />
      </div>

      <RewardFlow background="radial-gradient(125.2% 64.85% at 50.13% 44.75%, #48EDCB 27.71%, #1A9CA1 73.56%)" />

      <PlantDialog
        isOpen={isPlantDialogOpen()}
        close={() => setIsPlantDialogOpen(false)}
      />

      {/* Close button */}
      <button
        class="fixed left-4 top-[calc(16px+var(--safe-area-top))] grid size-9 place-items-center rounded-full bg-white/80 backdrop-blur-sm"
        onClick={() => native.general.close()}
      >
        <CloseIcon />
      </button>
    </>
  )
}
